/**
 * In-game HUD, deliberately tiny: credits + income (top-left), one alert line (top-centre),
 * our rank and score, the territory share bar and the match clock (top-right; under them the
 * match timeline with WEAPONS RELEASE and CAPITALS EXPOSED marked; the clock turns amber for the
 * final minute and counts the last 10 s big), build strip (bottom-centre) and — only when needed —
 * the selected-unit chip, the nation menu (diplomacy), the toasts for what matters to us (right,
 * under the clock: `ui/toasts.ts`), a brief caption by the pointer confirming an order, the Tab
 * scoreboard and the Esc menu (modal, behind a scrim that swallows all pointer input; RULES ·
 * CONTROLS inside — the keys live there, not on screen). Once eliminated: a full-screen moment, then the spectator HUD (alert line, share bar, Tab, LEAVE).
 */
import { BALANCE, buildCost, TICK_HZ, type BuildKind, type PlayerId, type PlayerView } from '@sa/sim';
import { betrayalTag, type DiplomacyBook, type MenuChoice, type MenuItem, type NationMenu } from '../state/diplomacy';
import type { AlertLine } from '../state/alerts';
import { BANK_CREDITS, FINAL_COUNT_TICKS, FINAL_MINUTE_TICKS, clock, leftAt } from '../state/alerts';
import { ourStanding, rulesSections, scoreboard, standingText } from '../state/score';
import { BUILD_LABEL, BUILD_ORDER, canAfford } from '../input/commands';
import { perMin, type Chip, type ChipAction } from '../input/actions';
import type { IncomeBreakdown } from '../state/economy';
import { drawGlyph2d } from '../render/glyphs';
import { drawPatternSwatch, type Palette } from '../render/palette';
import { COLORS, cssRgb } from '../theme';
import { fmtInt, h, kids, setText } from './dom';
import { NationMenuView } from './nationMenu';
import { ToastStack } from './toasts';
import type { ToastFrame } from '../state/toasts';
import { GUIDE_GOAL, type GuideLine } from '../state/guide';

export interface HudHooks {
  build(kind: BuildKind): void;
  action(a: ChipAction): void;
  /** a nation menu row (and, for SEND CREDITS, the amount) was chosen */
  nationAct(item: MenuItem, choice?: MenuChoice): void;
  /** a nation's row in the Tab board was pressed at screen (x, y): open its menu */
  nationRow(player: PlayerId, x: number, y: number): void;
  /** the pointer is on a nation's row in the Tab board (null: off the board): its land pulses on the map */
  nationHover(player: PlayerId | null): void;
  resume(): void;
  toggleMute(): boolean;
  /** the MUSIC button (cycles the tracks and OFF): its new words */
  cycleMusic(): string;
  surrender(): void;
  leave(): void;
  /** the alert line was clicked (jump to the threat) */
  alertClicked(): void;
  /** an action the HUD itself refuses (e.g. an unaffordable build slot) */
  error(msg: string): void;
  /** where our income comes from (the panel under the credits, on hover) */
  income(): IncomeBreakdown;
  /** a city's name for the income panel */
  cityName(id: number): string | null;
  /** the chip's title was pointed at (flash the selection marker) or clicked (and look at it if it is off screen) */
  chipTitle(click: boolean): void;
  /** SKIP on the first-match guide */
  skipGuide(): void;
}

interface Slot {
  kind: BuildKind;
  el: HTMLButtonElement;
  canvas: HTMLCanvasElement;
  price: HTMLSpanElement;
  /** under an unaffordable price: how far our credits get towards it */
  need: HTMLElement;
  state: string;
  affordable: boolean | null;
}

/** an order's confirmation by the pointer stays this long (ms; matches the CSS) */
const FLASH_MS = 1200;
/** below this viewport width (css px) the alert line's buttons sit on the row under it */
export const NARROW_PX = 1440;
/** the alert line keeps this far (css px) from the side clusters */
const ALERT_MARGIN = 24;
/** the knock-out moment collapses into the spectator HUD after this long (ms) */
export const ELIM_MS = 6000;
/** hold this long to confirm SURRENDER */
const HOLD_MS = 800;
/** income panel: cities listed by name before the rest are summed up */
const ECON_CITIES = 6;
/** a chip with more actions than this puts them on a second row */
const CHIP_ONE_ROW_ACTIONS = 4;
/** income panel refresh while it is open (ms) */
const ECON_REFRESH_MS = 500;

const SLOT_HINT: Record<BuildKind, string> = {
  radar: `SEES ${BALANCE.vision.radar} MU`,
  sam: `SHOOTS DOWN MISSILES · ${BALANCE.sam.range} MU`,
  // a silo is built empty: its missiles are bought one by one (F on the selected silo)
  silo: `ICBM ${fmtInt(BALANCE.silo.missileCost)} EACH (F)`,
  city: 'POPULATION · INCOME',
  airbase: 'FIGHTERS · BOMBERS',
  sub: `${BALANCE.sub.slbms} SLBMS · HIDDEN`,
  carrier: 'RADAR · SAM · FIGHTERS',
};

/** A build slot's hover tip: `SAM · SHOOTS DOWN MISSILES · 140 MU`; a silo says its price and that its ICBMs cost extra: `SILO 3,000 · ICBM 800 EACH (F)`. */
export function slotTip(kind: BuildKind): string {
  return kind === 'silo' ? `${BUILD_LABEL[kind]} ${fmtInt(buildCost(kind))} · ${SLOT_HINT[kind]}` : `${BUILD_LABEL[kind]} · ${SLOT_HINT[kind]}`;
}

export class Hud {
  readonly root: HTMLDivElement;
  private creditsBox = h('div.hud-credits');
  private credits = h('span.cr');
  private income = h('span.inc');
  /** where the income comes from: under the credits while the pointer is on them */
  private econ = h('div.hud-econ.panel');
  private econAt = -Infinity;
  private alert = h('div.hud-alert');
  private alertText = h('span');
  private alertTail = h('b.tail');
  /** the alert line's actions (brace offer, SAM focus), as buttons */
  private alertActs = h('span.alert-acts');
  private alertActKey = '';
  /** a one-time tip under the alert line */
  private alertSub = h('div.hud-alert-sub');
  /**
   * the row under the alert line: the tip, and — on screens narrower than `NARROW_PX` — the
   * line's buttons (they would run into the score caption beside a long line)
   */
  private alertRow2 = h('div.hud-alert-row2');
  private shareBox!: HTMLElement;
  /** what the alert line's width was last fitted for (text, buttons, viewport) */
  private alertFitKey = '';
  /** the line was too long for its room even in smaller type: its buttons went under it */
  private actsBelow = false;
  private leaveBtn = h('button.act.hud-leave', {}, 'LEAVE');
  private elim = h('div.hud-elim');
  /** CONNECTION LOST (online link trouble), over the dimmed world */
  private lost = h('div.hud-lost');
  private lostTitle = h('div.t');
  private lostSub = h('div.s');
  private lostActs = h('div.row-inline');
  private lostKey = '';
  private elimTimer: ReturnType<typeof setTimeout> | null = null;
  private spectating = false;
  /** our rank and score: `SCORE #3 · 87` */
  private scoreVal = h('b');
  private sharePct = h('span.pct', {}, h('span.lbl', {}, 'SCORE'), this.scoreVal);
  private shareBar = h('div.bar');
  /** the match so far, with WEAPONS RELEASE and CAPITALS EXPOSED marked */
  private timeFill = h('b.fill');
  private timeline = h('div.time');
  private clockEl = h('span.clock');
  /** the last 10 s, big */
  private countEl = h('div.hud-count');
  private countKey = '';
  /** an order's confirmation, briefly, by the pointer (`flash`) */
  private flashEl = h('div.hud-flash');
  private pointer = { x: 0, y: 0 };
  /** the Esc menu shows RULES · CONTROLS instead of its buttons */
  private rulesOpen = false;
  private menuArgs: [boolean, string, boolean, boolean] = [false, 'MUSIC · COLD WAR', true, false];
  private chip = h('div.hud-chip');
  private chipKey = '';
  /** the chip's layout (title + action keys) and whether it went to two rows: kept until the layout changes */
  private chipLayout = '';
  private chipTwo = false;
  private slots: Slot[] = [];
  private strip = h('div.hud-strip');
  /** the first-match guide (`state/guide.ts`): goal, then one step at a time */
  private guide = h('div.hud-guide.panel');
  private guideHead = h('span');
  private guideStep = h('div.step');
  private guideSkip = h('button.skip', {}, 'SKIP');
  private guideKey = '';
  private board = h('div.hud-board.panel');
  /** the news and offers to us, as toasts (right, under the clock) */
  private toasts: ToastStack;
  private nation: NationMenuView;
  /** holding right-click on a nation: the ring that fills until its menu opens */
  private hold = h('div.hud-hold');
  private scrim = h('div.hud-scrim');
  private menu = h('div.hud-menu.panel');
  private menuCaption = h('div.hud-menu-cap');
  private shareKey = '';
  private placing: BuildKind | null = null;

  constructor(
    private pal: Palette,
    private players: PlayerView['players'],
    private matchTicks: number,
    private hooks: HudHooks,
  ) {
    this.toasts = new ToastStack(pal);
    this.nation = new NationMenuView({ act: (item, choice) => hooks.nationAct(item, choice), refuse: (why) => hooks.error(why) });
    this.alert.append(h('i'), h('span.at', {}, this.alertText, this.alertTail), this.alertActs, h('i'));
    this.alertRow2.append(this.alertSub);
    this.alert.onclick = () => hooks.alertClicked();
    this.alertActs.hidden = true;
    this.alertSub.hidden = true;
    this.leaveBtn.onclick = () => hooks.leave();
    this.leaveBtn.hidden = true;
    this.elim.hidden = true;
    this.lost.append(this.lostTitle, this.lostSub, this.lostActs);
    this.lost.hidden = true;
    const strip = this.strip;
    BUILD_ORDER.forEach((kind, i) => {
      const canvas = h('canvas', { width: 44, height: 32 });
      const price = h('span.price', {}, fmtInt(buildCost(kind)));
      const need = h('b');
      const slot: Slot = { kind, el: null as unknown as HTMLButtonElement, canvas, price, need, state: '', affordable: null };
      slot.el = h(
        'button.slot',
        { onclick: () => this.slotClicked(slot) },
        h('span.key', {}, String(i + 1)),
        h('span.tip', {}, slotTip(kind)),
        canvas,
        h('span.name', {}, BUILD_LABEL[kind]),
        price,
        h('span.need', {}, need),
      );
      strip.append(slot.el);
      this.slots.push(slot);
    });
    this.creditsBox.append(this.credits, this.income);
    this.guideSkip.onclick = () => hooks.skipGuide();
    this.guide.append(h('div.head', {}, this.guideHead, this.guideSkip), h('div.goal', {}, GUIDE_GOAL), this.guideStep);
    this.guide.hidden = true;
    this.econ.hidden = true;
    this.creditsBox.onpointerenter = () => {
      this.econ.hidden = false;
      this.econAt = -Infinity;
    };
    this.creditsBox.onpointerleave = () => (this.econ.hidden = true);
    // the scrim swallows every click on the world; only its buttons (and Esc) act
    this.scrim.append(this.menuCaption, this.menu);
    this.root = h(
      'div.hud',
      {},
      h('div.hud-shade'),
      this.creditsBox,
      this.guide,
      this.econ,
      this.alert,
      this.alertRow2,
      (this.shareBox = h('div.hud-share', {}, this.sharePct, h('div.bars', {}, this.shareBar, this.timeline), this.clockEl)),
      this.countEl,
      this.flashEl,
      this.leaveBtn,
      this.chip,
      strip,
      this.toasts.el,
      this.board,
      this.hold,
      this.nation.el,
      this.elim,
      this.lost,
      this.scrim,
    );
    this.board.hidden = true;
    this.board.onpointerleave = () => hooks.nationHover(null);
    this.countEl.hidden = true;
    this.flashEl.hidden = true;
    // (where the pointer is: an order's confirmation shows there)
    window.addEventListener('pointermove', (e) => {
      const r = this.root.getBoundingClientRect();
      this.pointer = { x: e.clientX - r.left, y: e.clientY - r.top };
    });
    this.buildTimeline();
    this.hold.hidden = true;
    this.scrim.hidden = true;
    this.chip.hidden = true;
  }

  /** First-timers: the build slots say what they are (SAM, SILO …) under the glyph. */
  setNamed(on: boolean): void {
    this.strip.classList.toggle('named', on);
  }

  /** The first-match guide's line (null: hidden); its build slot, if any, is lit on the strip. */
  setGuide(line: GuideLine | null): void {
    const key = line ? `${line.n}|${line.text}|${line.slot}` : '';
    if (key === this.guideKey) return;
    this.guideKey = key;
    this.guide.hidden = !line;
    for (const s of this.slots) s.el.classList.toggle('guide', !!line && s.kind === line.slot);
    if (!line) return;
    this.guide.classList.toggle('outro', line.outro);
    setText(this.guideHead, line.outro ? 'FIRST MATCH · DONE' : `FIRST MATCH · STEP ${line.n} / ${line.of}`);
    this.guideSkip.hidden = line.outro;
    setText(this.guideStep, line.text);
  }

  setPlacing(kind: BuildKind | null): void {
    this.placing = kind;
  }

  private slotClicked(s: Slot): void {
    if (this.menuOpen) return;
    this.hooks.build(s.kind);
  }

  /** `allies`: the allies that still bind us (`GameStore.allies`). */
  update(v: PlayerView, allies: readonly PlayerId[], tick: number, alert: AlertLine | null, chip: Chip | null): void {
    setText(this.credits, fmtInt(v.credits));
    setText(this.income, `${v.incomePerMin >= 0 ? '+' : ''}${fmtInt(v.incomePerMin)}/min`);
    this.creditsBox.classList.toggle('bank', v.released && v.credits >= BANK_CREDITS);
    this.updateAlert(alert);
    const st = ourStanding(v);
    setText(this.scoreVal, st ? standingText(st) : '—');
    this.updateClock(v, tick);
    this.updateShareBar(v, allies);
    if (this.spectating) return;
    this.updateSlots(v);
    this.updateChip(chip);
    this.updateEcon();
  }

  private updateAlert(alert: AlertLine | null): void {
    this.updateAlertActions(alert?.actions ?? []);
    const sub = alert?.sub ?? '';
    this.alertSub.hidden = !sub;
    setText(this.alertSub, sub);
    // narrow (or a line too long for its room: `fitAlert`): the buttons go under the line, right of the tip
    const narrow = window.innerWidth < NARROW_PX;
    this.placeAlertActs(narrow || this.actsBelow);
    this.alertRow2.hidden = !sub && (this.alertActs.parentElement !== this.alertRow2 || this.alertActs.hidden || !alert);
    if (!alert) {
      this.alert.hidden = true;
      return;
    }
    setText(this.alertText, alert.text);
    const tail = alert.tail;
    this.alertTail.hidden = !tail;
    // (emptied too: a stale cue must not linger in the DOM — once the HUD is hidden at the end its
    // text is read whole, hidden parts included: "SPECTATING · 0:00 LEFT · R BRACE READY")
    if (!tail) setText(this.alertTail, '');
    else {
      setText(this.alertTail, ` · ${tail.text}`);
      const tc = `tail tone-${tail.tone}`;
      if (this.alertTail.className !== tc) this.alertTail.className = tc;
    }
    const cls = `hud-alert tone-${alert.tone}${alert.outline ? ' outline' : ''}${alert.strong ? ' strong' : ''}`;
    if (this.alert.className.replace(/ (tight|tighter)/g, '') !== cls) this.alert.className = cls;
    this.alert.hidden = false;
    this.fitAlert(`${alert.text}|${tail?.text ?? ''}|${cls}|${this.alertActKey}|${narrow}|${window.innerWidth}`);
  }

  /**
   * Keep the alert line between the side clusters (credits left; score, bar and clock right) with
   * a margin: its width is capped at what lies between them, and a line too long for that steps
   * its type down (`tight`, `tighter`) instead of running into them. Measured only when it changes.
   */
  private fitAlert(key: string): void {
    if (key === this.alertFitKey) return;
    this.alertFitKey = key;
    const root = this.root.getBoundingClientRect();
    const mid = root.width / 2;
    const left = this.creditsBox.getBoundingClientRect().right - root.left;
    const right = this.shareBox.getBoundingClientRect().left - root.left;
    const max = Math.max(240, 2 * Math.min(mid - left, right - mid) - 2 * ALERT_MARGIN);
    this.alert.style.maxWidth = `${max}px`;
    // (the row under it too: the tip gives way to its buttons)
    this.alertRow2.style.maxWidth = `${max}px`;
    this.alert.classList.remove('tight', 'tighter');
    this.actsBelow = false;
    if (window.innerWidth >= NARROW_PX) this.placeAlertActs(false);
    // (its words shrink before the box does: a clipped line is one too long)
    const at = this.alertText.parentElement!;
    const over = () => this.alert.scrollWidth > max || at.scrollWidth > at.clientWidth + 1;
    // step by step: smaller type (no side rules), the buttons under the line, smaller still
    if (over()) this.alert.classList.add('tight');
    if (over() && this.alertActs.parentElement === this.alert) {
      this.actsBelow = true;
      this.placeAlertActs(true);
      this.alertRow2.hidden = this.alertActs.hidden;
    }
    if (over()) this.alert.classList.add('tighter');
  }

  /** The alert line's buttons inline (wide) or on the row under it (`below`). */
  private placeAlertActs(below: boolean): void {
    const home = below ? this.alertRow2 : this.alert;
    if (this.alertActs.parentElement === home) return;
    if (below) this.alertRow2.append(this.alertActs);
    else this.alert.insertBefore(this.alertActs, this.alert.lastElementChild);
  }

  /** The alert line's buttons (e.g. "R BRACE 300", "E FOCUS SAMS"); a disabled one still says why when pressed. */
  private updateAlertActions(acts: readonly ChipAction[]): void {
    const key = acts.map((a) => `${a.key}|${a.label}|${a.enabled}|${a.done}|${a.cost ?? ''}`).join(',');
    const btns = [...this.alertActs.children] as HTMLButtonElement[];
    if (key !== this.alertActKey) {
      this.alertActKey = key;
      this.alertActs.hidden = !acts.length;
      this.alertActs.replaceChildren(
        ...acts.map((a) => h(`button.alert-act${a.done ? '.done' : a.enabled ? '' : '.off'}`, {}, ...actionContent(a))),
      );
      btns.length = 0;
      btns.push(...([...this.alertActs.children] as HTMLButtonElement[]));
    }
    // (actions carry fresh intents every frame: rebind)
    acts.forEach((a, i) => {
      if (btns[i]) btns[i].onclick = (e) => (e.stopPropagation(), a.done || this.hooks.action(a));
    });
  }

  /**
   * Spectator HUD (we were eliminated): no economy, build strip or orders — the alert line,
   * share bar + clock, Tab and a LEAVE button; the camera stays free.
   */
  setSpectating(on: boolean): void {
    if (on === this.spectating) return;
    this.spectating = on;
    this.root.classList.toggle('spectating', on);
    this.leaveBtn.hidden = !on;
    if (on) {
      this.chip.hidden = true;
      this.chipKey = '';
      this.closeNation();
    }
  }

  /**
   * The moment we are knocked out: a full-screen title over the dimmed world, then — after ~6 s
   * (a pointer merely resting on it does not hold it), or SPECTATE — the spectator HUD underneath.
   * One LEAVE at a time: the spectator HUD's own LEAVE waits until the moment has gone.
   */
  showEliminated(title: string, sub: string, ms = ELIM_MS): void {
    const close = () => {
      if (this.elimTimer) clearTimeout(this.elimTimer);
      this.elimTimer = null;
      this.elim.classList.add('out');
      this.root.classList.remove('elim-up');
      setTimeout(() => (this.elim.hidden = true), 600);
    };
    this.elim.classList.remove('out');
    this.elim.replaceChildren(
      h('div.t', {}, title),
      h('div.s', {}, sub),
      h('div.row-inline', {}, h('button.act', { onclick: close }, 'SPECTATE'), h('button.act.dim', { onclick: () => this.hooks.leave() }, 'LEAVE')),
    );
    this.elim.hidden = false;
    this.root.classList.add('elim-up');
    if (this.elimTimer) clearTimeout(this.elimTimer);
    this.elimTimer = setTimeout(close, ms);
  }

  /**
   * A full-screen moment or modal is up (the knock-out, the Esc menu, CONNECTION LOST): the map's
   * words step back under it (`canvas.labels.under`).
   */
  get overlayUp(): boolean {
    return (!this.elim.hidden && !this.elim.classList.contains('out')) || !this.lost.hidden || this.menuOpen;
  }

  /** The link to the server is down: orders are paused (the build strip and chip grey out). */
  setLinkDown(down: boolean): void {
    this.root.classList.toggle('linklost', down);
  }

  /** CONNECTION LOST and its buttons (RETRY / MENU); refreshed in place while it is up. */
  showLost(title: string, sub: string, actions: readonly { label: string; run(): void; primary?: boolean }[]): void {
    setText(this.lostTitle, title);
    setText(this.lostSub, sub);
    const key = `${title}|${actions.map((a) => a.label).join(',')}`;
    if (key !== this.lostKey) {
      this.lostKey = key;
      this.lostActs.replaceChildren(...actions.map((a) => h(`button.act${a.primary ? '' : '.dim'}`, { onclick: () => a.run() }, a.label)));
    }
    this.lost.hidden = false;
  }

  hideLost(): void {
    this.lost.hidden = true;
    this.lostKey = '';
  }

  /**
   * Affordable slots are bright; an unaffordable one keeps its real price, dimmed, with a thin
   * bar under it filling up as credits come in (MAX at the cap). A slot that just became
   * affordable pulses once.
   */
  private updateSlots(v: PlayerView): void {
    for (const s of this.slots) {
      const ok = canAfford(v, s.kind);
      const cost = buildCost(s.kind);
      const short = Math.max(0, cost - v.credits);
      const progress = Math.min(1, v.credits / cost);
      const st = `${ok ? 1 : 0}${this.placing === s.kind ? 'p' : ''}${ok ? '' : short > 0 ? Math.floor(progress * 40) : 'cap'}`;
      if (s.affordable === false && ok) {
        s.el.classList.remove('pulse');
        void s.el.offsetWidth; // restart the animation
        s.el.classList.add('pulse');
      }
      s.affordable = ok;
      if (st === s.state) continue;
      const glyphChanged = s.state[0] !== st[0];
      s.state = st;
      s.el.classList.toggle('off', !ok);
      s.el.classList.toggle('on', this.placing === s.kind);
      setText(s.price, ok || short > 0 ? fmtInt(cost) : 'MAX');
      s.need.parentElement!.hidden = ok || short <= 0;
      s.need.style.width = `${Math.round(progress * 100)}%`;
      if (!glyphChanged) continue;
      const ctx = s.canvas.getContext('2d')!;
      ctx.clearRect(0, 0, s.canvas.width, s.canvas.height);
      ctx.strokeStyle = ctx.fillStyle = cssRgb(COLORS.me, ok ? 0.9 : 0.3);
      drawGlyph2d(ctx, s.kind, 22, 16, 1.4);
    }
  }

  /** The match timeline under the share bar: WEAPONS RELEASE and CAPITALS EXPOSED where they fall. */
  private buildTimeline(): void {
    const mark = (tick: number, label: string) =>
      h('i', { style: { left: `${((tick / this.matchTicks) * 100).toFixed(2)}%` }, 'data-tick': String(tick) }, h('span', {}, label));
    const P = BALANCE.phases;
    this.timeline.replaceChildren(
      this.timeFill,
      ...kids(P.releaseTick < this.matchTicks ? mark(P.releaseTick, 'RELEASE') : null, P.capitalsExposedTick < this.matchTicks ? mark(P.capitalsExposedTick, 'CAPITALS') : null),
    );
  }

  /**
   * The match clock (time left: it decides the match): bright, amber for the final minute, and the
   * last 10 s counted big in the middle of the screen; the timeline fills as the match runs.
   */
  private updateClock(v: PlayerView, tick: number): void {
    const left = Math.max(0, this.matchTicks - tick);
    setText(this.clockEl, clock(left / TICK_HZ));
    this.clockEl.classList.toggle('final', left <= FINAL_MINUTE_TICKS && !v.result);
    this.timeFill.style.width = `${Math.min(100, (tick / this.matchTicks) * 100).toFixed(2)}%`;
    // only the next mark is named (two names would collide on a short bar); passed ones dim
    let next = true;
    for (const m of this.timeline.querySelectorAll('i')) {
      const past = tick >= Number((m as HTMLElement).dataset.tick);
      m.classList.toggle('past', past);
      m.classList.toggle('next', !past && next);
      if (!past) next = false;
    }
    const n = left > 0 && left <= FINAL_COUNT_TICKS && !v.result ? Math.ceil(left / TICK_HZ) : 0;
    const key = String(n);
    if (key === this.countKey) return;
    this.countKey = key;
    this.countEl.hidden = n === 0;
    // a fresh element each second restarts its beat
    if (n) this.countEl.replaceChildren(h('b', {}, String(n)));
  }

  private blocksAt = -Infinity;
  private blocksCache: { x: number; y: number; w: number; h: number }[] = [];

  /**
   * The HUD's blocks over the map (css px, relative to the HUD): the toast stack, the unit chip,
   * the nation menu, the Tab board, a tip under the alert line — the world's words
   * keep out of them. Measured at most every 120 ms.
   */
  blocks(nowMs: number): { x: number; y: number; w: number; h: number }[] {
    if (nowMs - this.blocksAt < 120) return this.blocksCache;
    this.blocksAt = nowMs;
    const root = this.root.getBoundingClientRect();
    const out: { x: number; y: number; w: number; h: number }[] = [];
    for (const el of [this.toasts.el, this.chip, this.nation.el, this.board, this.alertRow2, this.guide]) {
      if (el.hidden || !el.isConnected) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) out.push({ x: r.left - root.left, y: r.top - root.top, w: r.width, h: r.height });
    }
    this.blocksCache = out;
    return out;
  }

  /** The toast stack (`state/toasts.ts` frame), every frame. */
  updateToasts(f: ToastFrame): void {
    this.toasts.update(f);
  }

  /**
   * An order went through (ICBM AWAY · KAZAN, SENT 1,000 TO VULCAN): a short caption by the pointer
   * for ~1 s — confirmed where it was given, not in the news.
   */
  flash(text: string): void {
    const el = this.flashEl;
    el.hidden = false;
    // a fresh element each time restarts its fade
    el.replaceChildren(h('span', {}, text));
    const x = Math.min(this.pointer.x + 16, this.root.clientWidth - 16 - Math.min(420, text.length * 8));
    el.style.left = `${Math.max(16, x)}px`;
    el.style.top = `${Math.max(80, Math.min(this.pointer.y + 18, this.root.clientHeight - 140))}px`;
    if (this.flashTimer) clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => (el.hidden = true), FLASH_MS);
  }

  private flashTimer: ReturnType<typeof setTimeout> | null = null;

  private updateShareBar(v: PlayerView, allies: readonly PlayerId[]): void {
    const shares = v.shares.length ? v.shares : [{ playerId: v.me, areaShare: v.areaShare, score: 0 }];
    const key = shares.map((s) => `${s.playerId}:${s.areaShare.toFixed(3)}`).join(',') + allies.join(',') + (v.eliminated ? 'x' : '');
    if (key === this.shareKey) return;
    this.shareKey = key;
    const order = [...shares].sort((a, b) => rank(a.playerId) - rank(b.playerId) || b.areaShare - a.areaShare);
    function rank(p: PlayerId) {
      return v.eliminated ? 0 : p === v.me ? 0 : allies.includes(p) ? 1 : 2;
    }
    this.shareBar.replaceChildren(
      ...order.map((s) => {
        const friendly = !v.eliminated && (s.playerId === v.me || allies.includes(s.playerId));
        return h('b', { style: { width: `${(s.areaShare * 100).toFixed(2)}%`, background: cssRgb(this.pal.color(s.playerId), friendly ? 0.9 : 0.4) } });
      }),
    );
  }

  /**
   * The income panel (hover on the credits): stipend / land / pop / cities, each city by name with
   * its level and income, so what a level is worth reads at a glance.
   */
  private updateEcon(): void {
    if (this.econ.hidden) return;
    const now = performance.now();
    if (now - this.econAt < ECON_REFRESH_MS) return;
    this.econAt = now;
    const b = this.hooks.income();
    const row = (label: string, n: number, cls = '') => h(`div.row${cls}`, {}, h('span', {}, label), h('b', {}, perMin(n)));
    const shown = b.perCity.slice(0, ECON_CITIES);
    const rest = b.perCity.slice(ECON_CITIES);
    this.econ.replaceChildren(
      ...kids(
        h('div.row.head', {}, h('span', {}, 'INCOME'), h('b', {}, perMin(b.total))),
        row('STIPEND', b.stipend),
        row('LAND', b.land),
        row('POPULATION', b.pop),
        row(`CITIES ×${b.perCity.length}`, b.cities),
        ...shown.map((c) => row(`${this.hooks.cityName(c.id) ?? 'CITY'} · L${c.level}`, c.perMin, '.city')),
        rest.length ? row(`+${rest.length} MORE`, rest.reduce((a, c) => a + c.perMin, 0), '.city') : null,
        // a nation-wide multiplier: its own row, so a city reads the same here and in its chip
        b.mult > 1 ? row(`UNDERDOG BONUS ×${b.mult}`, b.bonus) : null,
      ),
    );
  }

  /**
   * The selected-unit chip: title, stats, actions on one line — or, when crowded (more than four
   * actions, or stats that would not fit), the stats on the first row and the actions on a second.
   * The numbers are never cut; the layout sticks while the selection's actions stay the same, so
   * the buttons do not jump as timers tick.
   */
  private updateChip(chip: Chip | null): void {
    const key = chip ? `${chip.title}|${chip.stats}|${chip.actions.map((a) => `${a.key}${a.label}${a.enabled}${a.done}${a.cost ?? ''}${a.note ?? ''}${a.tip ?? ''}`).join(',')}` : '';
    if (key === this.chipKey) return;
    this.chipKey = key;
    this.chip.hidden = !chip;
    if (!chip) return;
    const layout = `${chip.title}|${chip.actions.map((a) => a.key).join('')}`;
    if (layout !== this.chipLayout) {
      this.chipLayout = layout;
      this.chipTwo = chip.actions.length > CHIP_ONE_ROW_ACTIONS;
    }
    const stats = h('span.stats', {}, chip.stats);
    this.chip.replaceChildren(
      // the title points at the unit: hover flashes its marker, a click brings it into view
      h('span.title', { onpointerenter: () => this.hooks.chipTitle(false), onclick: () => this.hooks.chipTitle(true) }, chip.title),
      stats,
      h(
        'span.acts',
        {},
        ...chip.actions.map((a) =>
          a.done
            ? h(`span.act-done${a.progress !== undefined ? '.prog' : ''}`, {}, a.label, a.progress !== undefined ? h('i', { style: { width: `${Math.round(a.progress * 100)}%` } }) : null)
            : // disabled actions stay clickable: pressing one says why it is unavailable
              h(`button.act${a.enabled ? '' : '.off'}`, { onclick: () => this.hooks.action(a) }, ...actionContent(a), a.tip ? h('span.tip', {}, a.tip) : null),
        ),
      ),
    );
    this.chip.classList.toggle('two', this.chipTwo);
    // one row would cut the stats: two rows from now on (for this selection)
    if (!this.chipTwo && stats.scrollWidth > stats.clientWidth + 1) {
      this.chipTwo = true;
      this.chip.classList.add('two');
    }
  }

  /**
   * Tab: nations by score (the public 1 Hz standings, ranked as the results screen will rank them:
   * score, then player id), with their land share beside it and their territory swatch; allies
   * tagged, a critical capital tagged `CRIT 1:12` (§1.11), the knocked-out dimmed with the clock
   * when they fell (`OUT 9:54 LEFT`) — their score stays, as it does in the final standings.
   * Every nation has its row (8 at most); the columns are one grid across the rows, each as wide as
   * its widest cell, so a long callsign never runs into its tag. Pressing another nation's row
   * opens its nation menu.
   */
  showBoard(show: boolean, v: PlayerView, allies: readonly PlayerId[], out: ReadonlyMap<PlayerId, number>, book?: Pick<DiplomacyBook, 'betrayedAt' | 'isTraitor'>): void {
    this.board.hidden = !show;
    // the board takes the toasts' place while it is held
    this.toasts.el.hidden = show;
    if (!show) {
      this.hooks.nationHover(null);
      return;
    }
    const board = scoreboard(v);
    const row = new Map(board.map((r) => [r.playerId, r]));
    const at = (p: PlayerId) => {
      const k = board.findIndex((r) => r.playerId === p);
      return k < 0 ? Infinity : k;
    };
    const ranked = [...this.players].sort((a, b) => at(a.id) - at(b.id) || a.id - b.id);
    const tag = (p: PlayerId) => {
      const until = v.criticalCapitals.get(p);
      if (until !== undefined) return h('b.crit', {}, `CRIT ${clock((until - v.tick) / TICK_HZ)}`);
      if (allies.includes(p)) return 'ALLY';
      // the match remembers: who betrayed us (and when), and — quietly — who broke any alliance
      const bt = book ? betrayalTag(book, p, v.me, this.matchTicks) : null;
      return bt ? h(bt.us ? 'b.betrayed' : 'span.traitor', {}, bt.text) : '';
    };
    this.board.replaceChildren(
      h('div.row.head', {}, h('span.rank', {}, '#'), h('span.name', {}, 'NATION'), h('span.tag', {}, ''), h('span.val', {}, 'SCORE'), h('span.val.land', {}, 'LAND')),
      ...ranked.map((p, i) => {
        const rank = i + 1;
        const fell = out.get(p.id);
        const sw = h('canvas.sw', { width: 10, height: 10 });
        drawPatternSwatch(sw.getContext('2d')!, this.pal.pattern(p.id), this.pal.color(p.id));
        // another nation still in: its row opens its nation menu (diplomacy from anywhere on the map)
        const pick = p.id !== v.me && fell === undefined && !v.eliminated;
        const r = row.get(p.id);
        const el = h(
          `div.row${p.id === v.me ? '.me' : ''}${fell !== undefined ? '.out' : ''}${pick ? '.pick' : ''}`,
          { style: { color: cssRgb(this.pal.color(p.id), 0.9) } },
          h('span.rank', {}, String(rank)),
          h('span.name', {}, sw, p.name.toUpperCase()),
          h('span.tag', {}, fell !== undefined ? `OUT ${leftAt(fell, this.matchTicks)}` : tag(p.id)),
          h('span.val.score', {}, r ? String(r.score) : '—'),
          h('span.val.land', {}, fell !== undefined || !r ? '—' : `${(r.areaShare * 100).toFixed(1)}%`),
        );
        // its land lights up on the map while the pointer is on its row (the knocked-out hold none)
        if (fell === undefined) el.onpointerenter = () => this.hooks.nationHover(p.id);
        if (pick) {
          el.onpointerdown = (e) => {
            e.preventDefault();
            const b = el.getBoundingClientRect();
            const root = this.root.getBoundingClientRect();
            this.hooks.nationRow(p.id, b.left - root.left - 8, b.bottom - root.top);
          };
        }
        return el;
      }),
    );
  }

  /**
   * The nation menu for another nation, at screen (x, y): see `ui/nationMenu.ts`; the rows come
   * from `state/diplomacy.ts` (`nationMenu`).
   */
  showNation(m: NationMenu, x: number, y: number): void {
    if (this.menuOpen || this.spectating) return;
    this.hideHold();
    const sw = h('canvas', { width: 10, height: 10 });
    drawPatternSwatch(sw.getContext('2d')!, this.pal.pattern(m.player), this.pal.color(m.player));
    this.nation.open(m, cssRgb(this.pal.color(m.player), 0.95), sw, x, y, this.root);
  }

  /** Refresh the open nation menu (countdowns, an answered offer). */
  updateNation(m: NationMenu): void {
    this.nation.update(m);
  }

  /** The nation whose menu is open, or null. */
  get nationOpen(): PlayerId | null {
    return this.nation.player;
  }

  /** Close the nation menu; true if it was open. */
  closeNation(): boolean {
    return this.nation.close();
  }

  /** Holding right-click on a nation at (x, y): a ring fills over `ms` (null: the hold ended). */
  showHold(at: { x: number; y: number; ms: number } | null): void {
    if (!at) {
      this.hideHold();
      return;
    }
    this.hold.replaceChildren();
    // a fresh SVG restarts the fill animation
    this.hold.innerHTML = '<svg viewBox="0 0 26 26" width="26" height="26"><circle cx="13" cy="13" r="10" pathLength="100" transform="rotate(-90 13 13)"/></svg>';
    Object.assign(this.hold.style, { left: `${at.x}px`, top: `${at.y}px` });
    this.hold.style.setProperty('--hold', `${at.ms}ms`);
    this.hold.hidden = false;
  }

  private hideHold(): void {
    this.hold.hidden = true;
  }

  get menuOpen(): boolean {
    return !this.scrim.hidden;
  }

  /** RULES · CONTROLS in place of the Esc menu's buttons (BACK or Esc returns). */
  private showRules(): void {
    this.rulesOpen = true;
    this.menu.classList.add('rules');
    setText(this.menuCaption, 'RULES · CONTROLS');
    const [goal, timeline, controls] = rulesSections(this.matchTicks);
    this.menu.replaceChildren(
      h('div.cols', {}, h('div.rc', {}, rulesSection(goal.title, goal.rows), rulesSection(timeline.title, timeline.rows)), h('div.rc', {}, rulesSection(controls.title, controls.rows))),
      h('button.act', { onclick: () => this.menuBack() }, 'BACK'),
    );
  }

  /** Esc inside the menu: from RULES · CONTROLS back to the menu (true), else nothing to peel (false). */
  menuBack(): boolean {
    if (!this.rulesOpen || this.scrim.hidden) return false;
    this.showMenu(true, ...this.menuArgs);
    return true;
  }

  /** Esc menu over a dimmed world. `pauses`: offline (the match freezes) vs online (it runs on). */
  showMenu(show: boolean, muted: boolean, musicLabel: string, canSurrender: boolean, pauses: boolean): void {
    this.scrim.hidden = !show;
    this.rulesOpen = false;
    this.menu.classList.remove('rules');
    if (!show) return;
    this.menuArgs = [muted, musicLabel, canSurrender, pauses];
    setText(this.menuCaption, pauses ? 'PAUSED' : 'MENU · MATCH CONTINUES');
    this.menuCaption.classList.toggle('live', !pauses);
    const sound = (m: boolean) => `SOUND · ${m ? 'OFF' : 'ON'}`;
    const mute = h('button.act', {}, sound(muted));
    mute.onclick = () => setText(mute, sound(this.hooks.toggleMute()));
    const music = h('button.act', {}, musicLabel);
    music.onclick = () => setText(music, this.hooks.cycleMusic());
    this.menu.replaceChildren(
      ...kids(
        h('button.act', { onclick: () => this.hooks.resume() }, 'RESUME'),
        h('button.act', { onclick: () => this.showRules() }, 'RULES · CONTROLS'),
        mute,
        music,
        h('button.act.dim', { onclick: () => this.hooks.leave() }, 'LEAVE MATCH'),
        canSurrender ? holdButton('HOLD TO SURRENDER', () => this.hooks.surrender()) : null,
      ),
    );
  }
}

/** One RULES · CONTROLS section: its heading, then term / what rows (a blank term continues the row above). */
function rulesSection(title: string, rows: readonly [string, string][]): HTMLElement {
  return h('div.sec', {}, h('div.h', {}, title), ...rows.map(([k, v]) => h(`div.r${k ? '' : '.cont'}`, {}, h('b', {}, k), h('span', {}, v))));
}

/** Key, label and price of an action button (a done action is just its label). */
function actionContent(a: ChipAction): (Node | string)[] {
  if (a.done) return [a.label];
  return kids(h('span.key', {}, a.key.toUpperCase()), a.label, a.cost ? h('span.cost', {}, fmtInt(a.cost)) : null, a.note ? h('span.note', {}, a.note) : null);
}

/** A button that fires only after being held for `HOLD_MS` (a fill sweeps across it). */
function holdButton(label: string, fire: () => void): HTMLButtonElement {
  const b = h('button.act.dim.hold', {}, h('span.fill'), h('span.lbl', {}, label));
  let timer: ReturnType<typeof setTimeout> | null = null;
  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    b.classList.remove('holding');
  };
  b.style.setProperty('--hold', `${HOLD_MS}ms`);
  b.addEventListener('pointerdown', () => {
    cancel();
    b.classList.add('holding');
    timer = setTimeout(() => {
      cancel();
      fire();
    }, HOLD_MS);
  });
  b.addEventListener('pointerup', cancel);
  b.addEventListener('pointerleave', cancel);
  return b;
}
